import React, { useEffect } from "react";
import styled from "styled-components";
import { Wrapper } from "../../constants/common/StyledGlobalConstants";
import { addCurrencySymbol } from "../../util/negativeAmountHandler";
import { getCountryContact } from "../../components/common/CountryCode";
import { Colors } from "../../constants/Colors";
import PaymentStepper from "./PaymentStepper";
import BankDetails from "./BankDetails";
import { COMMON_CONSTANTS, BILLINGPAYMENTS_REDBANNER_CONSTANTS } from "../../constants/StringConstants";
import EmailLink from "../common/EmailLink";
import { pendoTrack } from "../../util/pendoUtil";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

export const ParentFlexContainer = styled(Wrapper)`
  display: flex;
  flex-direction: ${(props) => props.direction || "row"};
  align-items: ${(props) => props.align || "center"};
  justify-content: ${(props) => props.justify || "flex-start"};
  padding: ${(props) => props.padding || "0"};
  gap: ${(props) => props.gap || "0"};
  width: ${(props) => props.width || "auto"};
  height: ${(props) => props.height || "auto"};
  background: ${(props) => props.background || "none"};
  border-radius: ${(props) => props.borderRadius || "0"};
  flex: ${(props) => props.flex || "none"};
  order: ${(props) => props.order || "0"};
  flex-grow: ${(props) => props.flexGrow || "0"};
`;

export const TextWrapper = styled(Wrapper)`
  font-family: ${(props) => props.fontFamily || "neue-haas-grotesk-text"};
  font-style: normal;
  font-weight: ${(props) => props.fontWeight || 400};
  font-size: ${(props) => props.fontSize || "14px"};
  line-height: ${(props) => props.lineHeight || "20px"};
  color: ${Colors.black};
  text-align: ${(props) => props.textAlign || "left"};
  ${(props) => props.maxWidth && `max-width: ${props.maxWidth};`}
`;

const FlexContainer = styled(Wrapper)`
  display: flex;
  flex-direction: ${(props) => props.direction || "row"};
  align-items: center;
  justify-content: space-between;
  padding: 0;
  width: 100%;
  text-align: center;
  height: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    padding-right: 10px;
  }
`;

const StepWrapper = styled(Wrapper)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 50%;
  padding-right: ${(props) => (props.paddingRight ? props.paddingRight : "")};
  position: relative;

  @media (max-width: 768px) {
    width: 100%;
    align-items: flex-start;
  }
`;

const StepCircle = styled(Wrapper)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  background: ${(props) => props.background || Colors.black};
  border: ${(props) => props.border || "none"};
  border-radius: 50%;
  color: ${(props) => props.color || Colors.white};
  font-family: "neue-haas-grotesk-text";
  font-weight: 700;
  font-size: 12px;
  line-height: 15px;
  text-align: center;
  z-index: 1;
  margin-bottom: 8px;
`;

const StepLine = styled(Wrapper)`
  position: absolute;
  top: 12%;
  left: 52%;
  width: 95%;
  height: 1px;
  background: ${Colors.disabledText};
  z-index: 0;

  @media (max-width: 950px) and (min-width: 769px) {
    top: 10%;
    width: 94%;
  }

  @media (max-width: 768px) {
    top: 25%;
    width: 1px;
    height: 75%;
    left: 12px;
  }

  @media (max-width: 480px) {
    top: 24%;
  }
`;

const StepLabel = styled(Wrapper)`
  width: 100%;
  height: 18px;
  font-family: "neue-haas-grotesk-text";
  font-style: normal;
  font-weight: 700;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
  color: ${Colors.black};
  flex: none;
  order: 0;
  flex-grow: 1;
  margin-bottom: 8px;
  @media (max-width: 768px) {
    text-align: left;
    flex-grow: 0;
    margin-top: 3px;
    padding-left: 10px;
  }
`;

const StepDescription = styled(Wrapper)`
  font-family: "neue-haas-grotesk-text";
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  color: ${Colors.black};
  flex: none;
  order: 0;
  flex-grow: 1;
  width: 100%;
  height: 50px;
  gap: 0px;
  opacity: 1;
  @media (max-width: 768px) {
    text-align: left;
    height: auto;
    padding-left: 10px;
  }
  @media (max-width: 490px) {
    height: auto;
  }
  @media (min-width: 769px) and (max-width: 930px) {
    height: ${(props) => {
    switch (props.userLanguage) {
      case "de-DE":
      case "fr-FR":
      case "nl-NL":
        return "70px";
      default:
        return "50px";
    }
  }};
  }
`;

const shouldRenderBreak = (userLanguage, windowSize) => {
  const breakpoints = {
    "de-DE": 1220,
    "fr-FR": 1160,
    "nl-NL": 1140,
  };
  return windowSize.width > (breakpoints[userLanguage] || 1040);
};

const YELLOW_BANNER_ID = "yellow-banner-notification";
const ORANGE_BANNER_ID = "orange-banner-notification";

const SuspendedAccountNotification = ({
  invoiceNumber,
  t,
  windowSize,
  makePayment,
  totalOutstandingCharges,
  currencySymbol,
  regionId,
  bannerStatus,
  userLanguage,
  autoPayFlag,
  contractAccountId,
  isSuspendedAccNotification
}) => {
  let notificationMessage;
  if (bannerStatus.showYellowBanner) {
    notificationMessage = t("Payment received");
  } else if (bannerStatus.showOrangeBannerForNAandApac || bannerStatus.showOrangeBannerForEMEA) {
    notificationMessage = t(COMMON_CONSTANTS.ACCOUNT_SUSPENDED);
  } else {
    notificationMessage = null;
  }
  let bannerId = null;
  if (bannerStatus.showYellowBanner) {
    bannerId = YELLOW_BANNER_ID;
  } else if (bannerStatus.showOrangeBannerForNAandApac || bannerStatus.showOrangeBannerForEMEA) {
    bannerId = ORANGE_BANNER_ID;
  }

  const { accountUserTokenDetails } = useSelector((state) => state.ACCOUNT);
  const bankDetailsFromAPI = useSelector((state) => state.ACCOUNT?.bankDetails);
  const isAPACCOUNTRY = accountUserTokenDetails?.isAPAC;

  useEffect(() => {
    let eventName = "";
    let status = "";

    if (bannerStatus.showOrangeBannerForNAandApac) {
      eventName = "Customer Portal | Billing And Payment | Suspended Account Notification | Payment Not Done | NA and APAC";
      status = "Banner of Payment not done displayed for NA and APAC";
    } else if (bannerStatus.showOrangeBannerForEMEA) {
      eventName = "Customer Portal | Billing And Payment | Suspended Account Notification | Payment Not Done | EMEA";
      status = "Banner of Payment not done displayed for EMEA";
    } else if (bannerStatus.showYellowBanner) {
      eventName = "Customer Portal | Billing And Payment | Suspended Account Notification | Payment Done";
      status = "Banner of Payment done displayed";
    }

    if (eventName) {
      pendoTrack(eventName, {
        Status: status,
        ContractAccountID: contractAccountId,
      });
    }
  }, [bannerStatus.showOrangeBannerForNAandApac, bannerStatus.showOrangeBannerForEMEA, bannerStatus.showYellowBanner, contractAccountId]);

  return (
    <ParentFlexContainer
      id={bannerId}
      direction="column"
      align="center"
      padding="32px"
      gap="32px"
      background={bannerStatus.showYellowBanner ? Colors.lightYellow : Colors.lightPeach}
      borderRadius="16px"
      marginTop="64px"
      marginBottom="24px"
      aria-hidden={isSuspendedAccNotification}
    >
      <ParentFlexContainer direction="column" align="flex-start" padding="0px" gap="42px" width="100%">
        <ParentFlexContainer direction="column" align="flex-start" padding="0px" gap="21px" width="100%">
          <TextWrapper
            fontFamily="'neue-haas-grotesk-display"
            fontWeight={700}
            fontSize="32px"
            lineHeight="36px"
            textAlign="left"
            id="notification-title"
          >
            {notificationMessage}
          </TextWrapper>
          {bannerStatus.showYellowBanner && (
            <>
              <div
                style={{
                  fontFamily: "Neue Haas Grotesk Text Std",
                  fontStyle: "normal",
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "20px",
                  color: Colors.black,
                  textAlign: "left",
                }}
              >
                {t(
                  "Thank you for your payment. Your account now has temporary access to Reveal. It can take two business days for the balance below to be updated. Once we confirm the full balance is cleared, your account will be fully restored."
                )}
                {autoPayFlag?.autoPayNotify && (
                  <>
                    {t(` To avoid late payments, sign up for Auto Pay in `)}
                    <button
                      style={{
                        cursor: "pointer",
                        textDecorationLine: "underline",
                        textDecorationStyle: "solid",
                        textUnderlinePosition: "from-font",
                        textDecorationSkipInk: "none",
                        textUnderlineOffset: "3px",
                        background: "none",
                        border: "none",
                        padding: 0,
                        font: "inherit",
                        color: "inherit",
                      }}
                      onClick={() => makePayment("managewallet", invoiceNumber)}
                      aria-label={t("Manage Payment Methods")}
                      id="manage-payment-methods-yellowBanner"
                    >
                      {t("MANAGE PAYMENT METHODS")}
                    </button>
                    {"."}
                  </>
                )}
              </div>
              <PaymentStepper t={t} isFromOrangeBanner={false} />
            </>
          )}

          {bannerStatus.showOrangeBannerForNAandApac && (
            <>
              <TextWrapper fontWeight={700} fontSize="24px" lineHeight="32px" textAlign="left">
                {t("Total outstanding charges ")}
                {addCurrencySymbol(totalOutstandingCharges, currencySymbol)}
              </TextWrapper>
              <TextWrapper textAlign="left">
                {t(
                  "There are overdue outstanding charges on your account. To continue using Reveal, please pay the outstanding charges in full now."
                )}
                <br />
                {t("If you need assistance, contact us at ")}
                {getCountryContact(regionId).phone}
                {isAPACCOUNTRY && (
                  <>
                    {t(" or via email at ")}
                    <EmailLink email={t(getCountryContact(regionId)?.email)} color={Colors.black} fontWeight={700} />
                  </>
                )}
                .
              </TextWrapper>
              <PaymentStepper t={t} isFromOrangeBanner={true} makePayment={makePayment} invoiceNumber={invoiceNumber} />
            </>
          )}

          {bannerStatus.showOrangeBannerForEMEA && (
            <>
              <TextWrapper fontWeight={700} fontSize="24px" lineHeight="32px" textAlign="left">
                {t(BILLINGPAYMENTS_REDBANNER_CONSTANTS.OUTSTANDING_CHARGES)}
                {t(" ")}
                {addCurrencySymbol(totalOutstandingCharges, currencySymbol)}
              </TextWrapper>
              <TextWrapper textAlign="left">
                {t(BILLINGPAYMENTS_REDBANNER_CONSTANTS.PAY_FULL_AMOUNT)}
                <br />
                {t(BILLINGPAYMENTS_REDBANNER_CONSTANTS.CONTACT_US)}
                {t(" ")}
                <span style={{ fontWeight: 700 }}>{t(getCountryContact(regionId)?.phone, { ns: "redBallTranslation" })}</span>
                {t(" ")}
                {t(BILLINGPAYMENTS_REDBANNER_CONSTANTS.OR_AT)}
                {t(" ")}
                <EmailLink email={t(getCountryContact(regionId)?.email)} color={Colors.black} fontWeight={700} />.
              </TextWrapper>

              <BankDetails regionId={regionId} t={t} bankDetails={bankDetailsFromAPI} />

              <FlexContainer direction="row" align="center" justify="space-between" width="100%" style={{ flexDirection: "row" }}>
                <StepWrapper paddingRight="9px">
                  <StepCircle color={Colors.white} background={Colors.black} aria-current="step" aria-label={t("Step 1: Make a payment")}>
                    1
                  </StepCircle>
                  <StepLine />
                  <FlexContainer direction="row" align="center" padding="0px 4px" width="100%" flex="none" order="1" alignSelf="stretch" flexGrow="0">
                    <StepLabel>{t(BILLINGPAYMENTS_REDBANNER_CONSTANTS.MAKE_A_PAYMENT)}</StepLabel>
                    <StepDescription
                      userLanguage={userLanguage}
                      fontFamily="neue-haas-grotesk-text"
                      fontSize="14px"
                      left
                      fontWeight={400}
                      lineHeight="20px"
                      textUnderlinePosition="from-font"
                      textDecorationSkipInk="none"
                      width="100%"
                      gap="0px"
                      opacity="1"
                    >
                      {t(BILLINGPAYMENTS_REDBANNER_CONSTANTS.PROOF_OF_PAYMENT)}
                      {t(" ")}
                      <EmailLink email={t(getCountryContact(regionId)?.email)} color={Colors.black} fontWeight={700} />.
                    </StepDescription>
                  </FlexContainer>
                </StepWrapper>
                <StepWrapper>
                  <StepCircle
                    color={Colors.black}
                    background="none"
                    border={`1px solid ${Colors.black}`}

                    aria-label={t("Step 2: Account restored")}
                  >
                    2
                  </StepCircle>
                  <FlexContainer direction="row" align="center" padding="0px 4px" width="100%" flex="none" order="1" alignSelf="stretch" flexGrow="0">
                    <StepLabel>{t(BILLINGPAYMENTS_REDBANNER_CONSTANTS.ACCOUNT_RESTORED)}</StepLabel>
                    <StepDescription
                      userLanguage={userLanguage}
                      fontFamily="neue-haas-grotesk-text"
                      fontSize="14px"
                      left
                      fontWeight={400}
                      lineHeight="20px"
                      textUnderlinePosition="from-font"
                      textDecorationSkipInk="none"
                      width="100%"
                      gap="0px"
                      opacity="1"
                    >
                      {t(BILLINGPAYMENTS_REDBANNER_CONSTANTS.PAYMENT_CLEAR_TEXT)}
                      {t(" ")}
                      {shouldRenderBreak(userLanguage, windowSize) && <br />}
                      {t(BILLINGPAYMENTS_REDBANNER_CONSTANTS.ACCOUNT_RESTORED_TEXT)}
                    </StepDescription>
                  </FlexContainer>
                </StepWrapper>
              </FlexContainer>
            </>
          )}
        </ParentFlexContainer>
      </ParentFlexContainer>
    </ParentFlexContainer>
  );
};
SuspendedAccountNotification.propTypes = {
  invoiceNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  t: PropTypes.func.isRequired,
  windowSize: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  makePayment: PropTypes.func,
  totalOutstandingCharges: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currencySymbol: PropTypes.string,
  regionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  bannerStatus: PropTypes.shape({
    showYellowBanner: PropTypes.bool,
    showOrangeBannerForNAandApac: PropTypes.bool,
    showOrangeBannerForEMEA: PropTypes.bool,
  }).isRequired,
  userLanguage: PropTypes.string,
  autoPayFlag: PropTypes.shape({
    autoPayNotify: PropTypes.bool,
  }),
  contractAccountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isSuspendedAccNotification: PropTypes.bool,
};

export default SuspendedAccountNotification;
